/**
 * V17.539 (RBAC 2C, brief 2026-10-30-004): the one role control, in the sidebar's top slot — the slot
 * Phase 1 reserved (design record 2026-10-25-050, Rule 9). It replaces the top-bar RoleSwitcher on
 * desktop (expanded + rail) and in the phone drawer.
 *
 * Multi-role user: the current role is a button that opens a menu of every role (the current one
 * marked); picking one calls the existing `switchRole` (2B: this session only) and lands on that
 * role's default page. Single-role user: plain text, nothing opens.
 * Keyboard: Enter / Space / ArrowDown open with focus on the current role, arrows (wrapping), Home and
 * End move, Enter picks, Esc closes back to the trigger, Tab closes.
 * V17.540 (2E, 2C a11y): a MOUSE open also focuses the current role row (arrows/Esc work at once);
 * after a switch, focus returns to the trigger once the new menu has rendered; the expanded trigger is
 * named "ບົດບາດ / Role" + the role (aria-labelledby).
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { NAV_STRINGS, type NavLang } from './navStrings';

export type RoleControlVariant = 'expanded' | 'rail' | 'drawer';

export function SidebarRoleControl({ lang, variant, onSwitched }: { lang: NavLang; variant: RoleControlVariant; onSwitched?: () => void }) {
  const s = NAV_STRINGS[lang];
  const auth = useAuth();
  const { user, activeRoleName, switchRole } = auth;
  const availableRoles = auth.availableRoles ?? []; // a test double may omit it
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const focusOnOpen = useRef(false);
  const refocusTrigger = useRef(false);
  const labelId = useId();
  const nameId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const currentIndex = Math.max(0, availableRoles.findIndex((r) => r.role_id === user?.role_id));
  useEffect(() => {
    if (open && focusOnOpen.current) {
      focusOnOpen.current = false;
      itemRefs.current[currentIndex]?.focus();
    }
  }, [open, currentIndex]);

  // After a switch the trigger is disabled until `switching` clears; focus it then (the new role's menu
  // has rendered by this point). The phone drawer closes instead, and the Sheet restores its own focus.
  useEffect(() => {
    if (!switching && refocusTrigger.current) {
      refocusTrigger.current = false;
      triggerRef.current?.focus();
    }
  }, [switching, open]); // `open` too: a fast switch can batch switching true→false into one render

  if (!user) return null;

  const roleName = String(activeRoleName || user.role_name || String(user.role ?? '').replace(/_/g, ' '));
  const multi = availableRoles.length > 1;
  const touch = variant === 'drawer';

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const pick = async (roleId: string) => {
    if (roleId === user.role_id) {
      close(true);
      return;
    }
    setSwitching(true);
    try {
      const route = await switchRole(roleId);
      setOpen(false);
      refocusTrigger.current = !onSwitched;
      navigate(route, { replace: true });
      onSwitched?.();
    } catch (err) {
      console.error('Failed to switch role:', err);
    } finally {
      setSwitching(false);
    }
  };

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusOnOpen.current = true;
      setOpen(true);
    }
  };

  const onItemKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const n = availableRoles.length;
    const to = (j: number) => { e.preventDefault(); itemRefs.current[(j + n) % n]?.focus(); };
    if (e.key === 'ArrowDown') to(i + 1);
    else if (e.key === 'ArrowUp') to(i - 1);
    else if (e.key === 'Home') to(0);
    else if (e.key === 'End') to(n - 1);
    else if (e.key === 'Escape') { e.preventDefault(); close(true); }
    else if (e.key === 'Tab') setOpen(false);
  };

  const menu = open && (
    <div
      role="menu"
      aria-label={s.switchRole}
      className={cn(
        'z-50 rounded-lg border border-sidebar-pop-border bg-sidebar-pop py-1 shadow-lg',
        variant === 'rail' && 'absolute left-full top-2 ml-2 w-64',
        variant === 'expanded' && 'absolute left-0 right-0 top-full mt-1',
        variant === 'drawer' && 'mt-2',
      )}
    >
      <p className="px-3 pb-1 pt-1.5 text-xs font-semibold text-sidebar-muted" aria-hidden="true">{s.switchRole}</p>
      {availableRoles.map((role, i) => {
        const current = role.role_id === user.role_id;
        return (
          <button
            key={role.role_id}
            ref={(el) => { itemRefs.current[i] = el; }}
            type="button"
            role="menuitem"
            aria-current={current ? 'true' : undefined}
            disabled={switching}
            onClick={() => void pick(role.role_id)}
            onKeyDown={(e) => onItemKey(e, i)}
            className={cn(
              'flex w-full items-center justify-between gap-2 px-3 text-left text-sm leading-snug focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sidebar-ring',
              touch ? 'min-h-[44px] py-2' : 'min-h-[36px] py-1.5',
              current ? 'bg-sidebar-active-bg font-medium text-sidebar-active-fg' : 'text-sidebar-text hover:bg-sidebar-hover',
              switching && 'cursor-wait opacity-60',
            )}
          >
            <span className="line-clamp-2" title={role.role_name}>{role.role_name}</span>
            {current && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );

  if (variant === 'rail') {
    if (!multi) {
      return (
        <div className="flex h-16 items-center justify-center border-b border-sidebar-border" title={`${s.brand} · ${roleName}`} data-testid="role-control">
          <span className="text-sm font-bold text-sidebar-strong">VTW</span>
        </div>
      );
    }
    return (
      <div ref={rootRef} className="relative flex h-16 items-center justify-center border-b border-sidebar-border" data-testid="role-control">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              ref={triggerRef}
              type="button"
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label={`${s.role}: ${roleName}`}
              onClick={() => { focusOnOpen.current = !open; setOpen((v) => !v); }}
              onKeyDown={onTriggerKey}
              className="flex h-11 w-14 items-center justify-center gap-0.5 rounded-xl text-sm font-bold text-sidebar-strong hover:bg-sidebar-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            >
              VTW
              <ChevronDown data-icon="chevron" className="h-3 w-3 text-sidebar-muted" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{roleName}</TooltipContent>
        </Tooltip>
        {menu}
      </div>
    );
  }

  const nameClass = 'line-clamp-2 text-left text-sm font-medium text-sidebar-strong';
  return (
    <div ref={rootRef} className="relative" data-testid="role-control">
      <p id={labelId} className="text-xs text-sidebar-muted">{s.role}</p>
      {multi ? (
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-labelledby={`${labelId} ${nameId}`}
          disabled={switching}
          onClick={() => { focusOnOpen.current = !open; setOpen((v) => !v); }}
          onKeyDown={onTriggerKey}
          className={cn(
            '-mx-1 mt-0.5 flex w-[calc(100%+0.5rem)] items-center justify-between gap-2 rounded-md px-1 hover:bg-sidebar-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
            touch && 'min-h-[44px]',
          )}
        >
          <span id={nameId} className={nameClass} title={roleName}>{roleName}</span>
          {switching ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-sidebar-muted" aria-hidden="true" />
          ) : (
            <ChevronDown data-icon="chevron" className={cn('h-4 w-4 shrink-0 text-sidebar-muted transition-transform', open && 'rotate-180')} aria-hidden="true" />
          )}
        </button>
      ) : (
        <p className={cn(nameClass, 'mt-0.5')} title={roleName}>{roleName}</p>
      )}
      {menu}
    </div>
  );
}
